import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["newFolderModal", "newFolderInput", "itemRow", "dropZone", "dropOverlay", "moveModal", "fileInput", "itemName"]
  static values = { folderId: String, confirmMove: String, rootName: String }

  connect() {
    this._onToggleNewFolder = () => this.toggleNewFolder()
    this._onUploadFiles = (e) => { if (e.detail?.files) this.uploadFiles(e.detail.files) }
    document.addEventListener("drive:toggle-new-folder", this._onToggleNewFolder)
    document.addEventListener("drive:upload-files", this._onUploadFiles)
  }

  disconnect() {
    document.removeEventListener("drive:toggle-new-folder", this._onToggleNewFolder)
    document.removeEventListener("drive:upload-files", this._onUploadFiles)
  }

  // --- Nowy folder ---

  toggleNewFolder() {
    const modal = this.newFolderModalTarget
    modal.classList.toggle("hidden")
    if (!modal.classList.contains("hidden")) {
      this.newFolderInputTarget.value = ""
      this.newFolderInputTarget.focus()
    }
  }

  // --- Drag & drop wierszy (przenoszenie do folderu) ---

  dragstart(event) {
    const row = event.currentTarget
    event.dataTransfer.setData("item-id", row.dataset.itemId)
    event.dataTransfer.setData("item-type", row.dataset.itemType)
    event.dataTransfer.effectAllowed = "move"
    row.classList.add("opacity-30")
  }

  dragover(event) {
    const row = event.currentTarget
    if (row.dataset.itemType === "folder") {
      event.preventDefault()
      event.dataTransfer.dropEffect = "move"
      row.classList.add("bg-blue-50", "ring-2", "ring-inset", "ring-blue-400")
    }
  }

  dragleave(event) {
    event.currentTarget.classList.remove("bg-blue-50", "ring-2", "ring-inset", "ring-blue-400")
  }

  drop(event) {
    event.preventDefault()
    const targetRow = event.currentTarget
    targetRow.classList.remove("bg-blue-50", "ring-2", "ring-inset", "ring-blue-400")

    if (targetRow.dataset.itemType !== "folder") return

    const itemId = event.dataTransfer.getData("item-id")
    const itemType = event.dataTransfer.getData("item-type")
    const targetFolderId = targetRow.dataset.itemId

    // nie przenosimy folderu do samego siebie
    if (itemType === "folder" && itemId === targetFolderId) return

    const itemName = this.getItemName(itemId, itemType)
    const targetName = this.getItemName(targetFolderId, "folder")
    if (!this.confirmMove(itemName, targetName)) return

    this.moveItem(itemType, itemId, targetFolderId)
  }

  // --- Move modal ---

  showMoveModal(event) {
    this._moveItemId = event.currentTarget.dataset.itemId
    this._moveItemType = event.currentTarget.dataset.itemType
    this.moveModalTarget.classList.remove("hidden")
  }

  hideMoveModal() {
    this.moveModalTarget.classList.add("hidden")
    this._moveItemId = null
    this._moveItemType = null
  }

  moveToFolder(event) {
    const targetFolderId = event.currentTarget.dataset.targetFolderId
    if (this._moveItemId) {
      const itemName = this.getItemName(this._moveItemId, this._moveItemType)
      const targetName = targetFolderId
        ? event.currentTarget.textContent.trim()
        : this.rootNameValue
      if (!this.confirmMove(itemName, targetName)) return
      this.moveItem(this._moveItemType, this._moveItemId, targetFolderId)
    }
    this.hideMoveModal()
  }

  getItemName(id, type) {
    const el = this.itemNameTargets.find(
      (el) => el.dataset.itemId === id && el.dataset.itemType === type,
    )
    return el ? el.textContent.trim() : ""
  }

  confirmMove(itemName, targetName) {
    const message = this.confirmMoveValue
      .replace("%{item}", itemName)
      .replace("%{folder}", targetName)
    return confirm(message)
  }

  moveItem(type, id, targetFolderId) {
    const csrfToken = document.querySelector("[name=\"csrf-token\"]").content
    fetch("/drive/move", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ type, id, target_folder_id: targetFolderId }),
    }).then((response) => {
      if (response.ok) {
        window.location.reload()
      }
    }).catch(() => {
      window.location.reload()
    })
  }

  // --- Rename ---

  rename(event) {
    const itemId = event.currentTarget.dataset.itemId
    const itemType = event.currentTarget.dataset.itemType

    const nameEl = this.itemNameTargets.find(
      (el) => el.dataset.itemId === itemId && el.dataset.itemType === itemType,
    )
    if (!nameEl) return

    const originalName = nameEl.textContent.trim()
    const input = document.createElement("input")
    input.type = "text"
    input.value = originalName
    input.className = "block w-full rounded-md border-0 py-0.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"

    const parent = nameEl.parentNode
    parent.replaceChild(input, nameEl)
    input.focus()
    input.select()

    let saved = false
    const save = () => {
      if (saved) return
      saved = true
      input.removeEventListener("blur", save)
      const newName = input.value.trim()
      if (newName && newName !== originalName) {
        const csrfToken = document.querySelector("[name=\"csrf-token\"]").content
        fetch("/drive/rename", {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ type: itemType, id: itemId, name: newName }),
        }).then((response) => {
          if (response.ok) {
            return response.json()
          }
          throw new Error("Rename failed")
        }).then(() => {
          window.location.reload()
        }).catch(() => {
          nameEl.textContent = originalName
          parent.replaceChild(nameEl, input)
        })
      } else {
        parent.replaceChild(nameEl, input)
      }
    }

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault()
        save()
      } else if (e.key === "Escape") {
        saved = true
        input.removeEventListener("blur", save)
        parent.replaceChild(nameEl, input)
      }
    })
    input.addEventListener("blur", save)
  }

  // --- Upload plików z systemu (drag & drop na stronę) ---

  // sprawdza czy to drag plików z systemu (nie wewnętrzny drag wierszy)
  isExternalFileDrag(event) {
    return event.dataTransfer.types.includes("Files") && !event.dataTransfer.types.includes("item-id")
  }

  fileDragEnter(event) {
    if (!this.isExternalFileDrag(event)) return
    event.preventDefault()
    if (this.hasDropOverlayTarget) {
      this.dropOverlayTarget.classList.remove("hidden")
    }
  }

  fileDragOver(event) {
    if (!this.isExternalFileDrag(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
  }

  fileDragLeave(event) {
    if (event.currentTarget === event.target && this.hasDropOverlayTarget) {
      this.dropOverlayTarget.classList.add("hidden")
    }
  }

  fileDrop(event) {
    if (this.hasDropOverlayTarget) {
      this.dropOverlayTarget.classList.add("hidden")
    }

    // wewnętrzny drag wierszy — ignorujemy
    if (!this.isExternalFileDrag(event)) return
    event.preventDefault()

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.uploadFiles(files)
    }
  }

  // --- Upload plików z inputa ---

  fileSelected(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.uploadFiles(files)
    }
  }

  uploadFiles(files) {
    const csrfToken = document.querySelector("[name=\"csrf-token\"]").content
    const formData = new FormData()

    for (const file of files) {
      formData.append("files[]", file)
    }
    if (this.folderIdValue) {
      formData.append("folder_id", this.folderIdValue)
    }

    // spinner
    this.showUploadSpinner(files.length)

    fetch("/drive/upload", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, Accept: "application/json" },
      redirect: "follow",
      body: formData,
    }).then(() => {
      window.location.reload()
    }).catch(() => {
      window.location.reload()
    })
  }

  showUploadSpinner(count) {
    const overlay = document.createElement("div")
    overlay.className = "fixed inset-0 z-50 bg-white/80 flex items-center justify-center"
    overlay.innerHTML = `
      <div class="text-center">
        <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="mt-3 text-sm font-medium text-gray-700">${count > 1 ? `Wgrywanie ${count} plików...` : "Wgrywanie pliku..."}</p>
      </div>
    `
    document.body.appendChild(overlay)
  }
}
